import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { MODULES } from '@woovi/roles';
import { CHARGE_STATUS_ENUM } from '@woovi/shared';

import type { PaymentMethodCreditCardSelected_charge$key } from './__generated__/PaymentMethodCreditCardSelected_charge.graphql.ts';
import type { PaymentMethodCreditCardSelected_query$key } from './__generated__/PaymentMethodCreditCardSelected_query.graphql.ts';
import PaymentActiveWaitingPaymentMethodCreditCard from './PaymentActiveWaitingPaymentMethodCreditCard.tsx';
import PaymentMethodCreditCardSelectedProcessing from './PaymentMethodCreditCardSelectedProcessing.tsx';
import PaymentMethodCreditCardSteps from './PaymentMethodCreditCardSteps.tsx';
import { useChargePaymentMethod } from '../../../charge/useChargePaymentMethod.tsx';
import PaymentCompleted from '../../completed/PaymentCompleted.tsx';
import PaymentMethodSelectedCustomerRegister from '../../paymentMethod/PaymentMethodSelectedCustomerRegister.tsx';

type PaymentMethodCreditCardSelectedProps = {
  charge: PaymentMethodCreditCardSelected_charge$key;
  query: PaymentMethodCreditCardSelected_query$key;
};

const PaymentMethodCreditCardSelected = (
  props: PaymentMethodCreditCardSelectedProps,
): ReactNode => {
  const query = useFragment<PaymentMethodCreditCardSelected_query$key>(
    graphql`
      fragment PaymentMethodCreditCardSelected_query on Query {
        ...PaymentCompleted_query
      }
    `,
    props.query,
  );

  const charge = useFragment<PaymentMethodCreditCardSelected_charge$key>(
    graphql`
      fragment PaymentMethodCreditCardSelected_charge on Charge {
        id
        status
        company {
          features
        }
        customer {
          id
        }
        ...PaymentCompleted_charge
        ...PaymentActiveWaitingPaymentMethodCreditCard_charge
        ...PaymentMethodCreditCardSelectedProcessing_charge
        ...PaymentMethodSelectedCustomerRegister_charge
        ...useChargePaymentMethod_charge
        ...PaymentMethodCreditCardSteps_charge
      }
    `,
    props.charge,
  );

  const { company } = charge;

  const isCreditCardSteps = company?.features?.includes(
    MODULES.CREDIT_CARD_STEPS_UI,
  );

  const { creditCard } = useChargePaymentMethod(charge);

  const isProcessing =
    creditCard.method && charge.status === CHARGE_STATUS_ENUM.ACTIVE;

  if (isProcessing) {
    return <PaymentMethodCreditCardSelectedProcessing charge={charge} />;
  }

  if (charge.status === CHARGE_STATUS_ENUM.COMPLETED) {
    return <PaymentCompleted charge={charge} query={query} />;
  }

  if (isCreditCardSteps) {
    return <PaymentMethodCreditCardSteps charge={charge} />;
  }

  if (!charge?.customer?.id) {
    return <PaymentMethodSelectedCustomerRegister charge={charge} />;
  }

  return <PaymentActiveWaitingPaymentMethodCreditCard charge={charge} />;
};

export default PaymentMethodCreditCardSelected;
